import React from 'react';

import { parseJsonlToSuggestionList } from '@aline/common';
import { gql } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Skeleton } from '@mui/material';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useSelectedSuggestionUnit } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';
import { makeStyles } from 'tss-react/mui';

import { SuggestionError, SuggestionItem } from './SuggestionItem';
import { SuggestionList } from './SuggestionList';
import { suggestionsWithStatusVar } from '../../../ChatPanelMarkdown';
import type { MarkdownComponentProps } from '../../../ChatPanelMarkdown';
import type { CodeBlockInfo } from '../../../remark-plugins/remarkCodeBlock';
import { getCodeBlockInfoFromNode } from '../../../remark-plugins/remarkCodeBlock';

void gql`
  query GetSuggestionDocument($documentId: UUID!) {
    document(documentId: $documentId) {
      __typename
      id
      name
    }
  }
`;

const useStyles = makeStyles()(theme => ({
  skeleton: {
    borderRadius: 16,
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0, 0),
  },
}));

export const GeneratingSuggestion = React.memo(function GeneratingSuggestion(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={clsx(classes.skeletonContainer)}>
      <Skeleton className={classes.skeleton} height={200} variant="rectangular" width="100%" />
    </div>
  );
});

export const ParsedSuggestionListElement = React.memo(function ParsedSuggestionListElement({
  code,
  isNodeStreaming,
  messageId,
  threadId,
}: CodeBlockInfo): JSX.Element | null {
  const psrsedItems = parseJsonlToSuggestionList(code, {
    /**
     * Drift in the editor's emitted-edit JSON shape used to silently
     * drop entire suggestions, leaving the user with a UI that shows
     * fewer items than the agent produced. Send the top-level keys
     * (NOT the line content — keep edit text out of Sentry) so on-call
     * can spot a schema-evolution gap.
     */
    onSchemaMiss: ({ parsedKeys }) => {
      Sentry.addBreadcrumb({
        category: 'chat-harness',
        data: { messageId, parsedKeys },
        level: 'warning',
        message: 'suggestion-code-parser: line parsed JSON but matched neither schema',
      });
    },
  });
  const { selectedSuggestionUnit } = useSelectedSuggestionUnit();
  const suggestionsWithStatusLookup = useReactiveVar(suggestionsWithStatusVar);
  const allSuggestionsWithStatus =
    messageId in suggestionsWithStatusLookup ? suggestionsWithStatusLookup[messageId] : null;
  const uniqIds = new Set(
    psrsedItems.flatMap(item => {
      return item.type === 'edit' ? [item.groupId] : [item.id];
    })
  );
  const suggestionsWithStatus =
    allSuggestionsWithStatus?.filter(item =>
      uniqIds.has(item.type === 'suggestion' ? item.suggestion.groupId : item.error.id)
    ) ?? [];
  const firstLoadedItem = suggestionsWithStatus.at(0);

  return (
    <SuggestionList
      firstLoadedSuggestion={
        firstLoadedItem?.type === 'suggestion' ? firstLoadedItem.suggestion : null
      }
      isLoading={isNodeStreaming}
      messageId={messageId}
      threadId={threadId}
    >
      {suggestionsWithStatus.map((item, index) => {
        switch (item.type) {
          case 'suggestion':
            return (
              <SuggestionItem
                key={index}
                messageId={messageId}
                selectedGroupId={selectedSuggestionUnit?.groupId ?? null}
                suggestionAndStatus={item}
                threadId={threadId}
              />
            );
          case 'error':
            return <SuggestionError key={index} error={item.error} />;
        }
      })}
      {isNodeStreaming && <GeneratingSuggestion />}
    </SuggestionList>
  );
});

export const CodeSuggestionListElement = React.memo(function CodeSuggestionListElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const codeBlockInfo = getCodeBlockInfoFromNode(node);

  if (!codeBlockInfo) return null;

  return <ParsedSuggestionListElement {...codeBlockInfo} />;
});
